import {
  AgeRangeType,
  AgeRangeTypeValue,
  CreatorAttributeFilters,
  DeviceCriterion,
  DeviceCriterionValue,
  GenderType,
  GenderTypeValue,
  IncomeRangeType,
  IncomeRangeTypeValue,
  ParentalStatusType,
  ParentalStatusTypeValue,
  PartnershipOpportunity,
  YouTubeChannelInsights
} from '@blisspointmedia/bpm-types/dist/YoutubeCreatorInsights';
import {
  AgeRange,
  AudienceInsightsDimensions,
  Gender
} from '@blisspointmedia/bpm-types/dist/YoutubePlanning';
import { MenuItem } from '@Components/Dropdown/dropdown-constants';
import { AudienceInsightMenuItem } from '../youtube-planning-types';

// The three CreatorFilterState modes. The values are CreatorFilterState's keys.
export const SearchMode = {
  ATTRIBUTES: 'attributes',
  TOPICS: 'topicEntityIds',
  CHANNELS: 'channelIds'
} as const;
type SearchModeValue = (typeof SearchMode)[keyof typeof SearchMode];

// Audience matches who watches a creator; Topics matches what the creator's videos are about.
export const AUDIENCE_SEARCH_TOOLTIP =
  'Finds creators whose viewers have these interests and demographics, whatever their videos ' +
  'are about. Use it to reach a type of person.';
export const TOPIC_SEARCH_TOOLTIP =
  'Finds creators who make videos about these topics, brands or products, whoever watches ' +
  'them. Use it to match the content of the videos.';

export const SEARCH_MODE_OPTIONS: { key: SearchModeValue; label: string; tooltip: string }[] = [
  {
    key: SearchMode.TOPICS,
    label: 'Topics',
    tooltip: TOPIC_SEARCH_TOOLTIP
  },
  {
    key: SearchMode.ATTRIBUTES,
    label: 'Audience',
    tooltip: AUDIENCE_SEARCH_TOOLTIP
  },
  {
    key: SearchMode.CHANNELS,
    label: 'Channels',
    tooltip: 'Look up specific YouTube channels by channel ID'
  }
];

// Every mode's inputs are kept while the user switches between them; only the active mode is
// searched.
export interface CreatorSearchDraft {
  mode: SearchModeValue;
  attributes: CreatorAttributeFilters;
  topicEntityIds: string[];
  channelIds: string[];
}

export const EMPTY_DRAFT: CreatorSearchDraft = {
  mode: SearchMode.ATTRIBUTES,
  attributes: {},
  topicEntityIds: [],
  channelIds: []
};

// Display labels for the ids in a search, keyed by id. Kept beside CreatorFilterState (which holds
// only ids, since it's also what a saved set stores) so a shared link shows names, not ids.
export type CreatorSearchLabels = Record<string, string>;

export const URL_PARAMS = {
  SEARCH: 'search',
  LABELS: 'labels'
} as const;

// The interest/topic id lists in CreatorAttributeFilters, by the typeahead dimension that fills
// them.
export const INTEREST_ID_KEYS = ['entityIds', 'categoryIds', 'userInterestIds'] as const;
export type InterestIdKey = (typeof INTEREST_ID_KEYS)[number];
export const INTEREST_KEY_BY_DIMENSION: Partial<Record<string, InterestIdKey>> = {
  [AudienceInsightsDimensions.KNOWLEDGE_GRAPH]: 'entityIds',
  [AudienceInsightsDimensions.CATEGORY]: 'categoryIds',
  [AudienceInsightsDimensions.AFFINITY_USER_INTEREST]: 'userInterestIds',
  [AudienceInsightsDimensions.IN_MARKET_USER_INTEREST]: 'userInterestIds'
};
export const INTEREST_DIMENSIONS = [
  AudienceInsightsDimensions.KNOWLEDGE_GRAPH,
  AudienceInsightsDimensions.CATEGORY,
  AudienceInsightsDimensions.AFFINITY_USER_INTEREST,
  AudienceInsightsDimensions.IN_MARKET_USER_INTEREST
];
export const TOPIC_DIMENSIONS = [AudienceInsightsDimensions.KNOWLEDGE_GRAPH];

export const TYPEAHEAD_DEBOUNCE_MS = 300;

// YouTube channel ids are "UC" followed by 22 URL-safe base64 characters.
export const CHANNEL_ID_PATTERN = /^UC[\w-]{22}$/;
// A user interest id as getAudienceInsights returns it: the numeric tail of the resource name.
export const USER_INTEREST_ID_PATTERN = /^\d+$/;
// A Knowledge Graph machine id: /m/… or /g/….
export const ENTITY_ID_PATTERN = /^\/[mg]\/[\w-]+$/;

// Loads typeahead options for the given Google audience dimensions.
export type AudienceOptionFetcher = (
  input: string,
  dimensions: AudienceInsightsDimensions[]
) => Promise<AudienceInsightMenuItem[]>;

export const AGE_RANGE_OPTIONS: MenuItem<AgeRangeTypeValue>[] = [
  { value: AgeRangeType.AGE_RANGE_18_24, label: AgeRange.AGE_RANGE_18_24 },
  { value: AgeRangeType.AGE_RANGE_25_34, label: AgeRange.AGE_RANGE_25_34 },
  { value: AgeRangeType.AGE_RANGE_35_44, label: AgeRange.AGE_RANGE_35_44 },
  { value: AgeRangeType.AGE_RANGE_45_54, label: AgeRange.AGE_RANGE_45_54 },
  { value: AgeRangeType.AGE_RANGE_55_64, label: AgeRange.AGE_RANGE_55_64 },
  { value: AgeRangeType.AGE_RANGE_65_UP, label: AgeRange.AGE_RANGE_65_UP }
];

export const GENDER_OPTIONS: MenuItem<GenderTypeValue>[] = [
  { value: GenderType.MALE, label: Gender.MALE },
  { value: GenderType.FEMALE, label: Gender.FEMALE }
];

export const PARENTAL_STATUS_OPTIONS: MenuItem<ParentalStatusTypeValue>[] = [
  { value: ParentalStatusType.PARENT, label: 'Parent' },
  { value: ParentalStatusType.NOT_A_PARENT, label: 'Not a parent' }
];

// Google's household income tiers are percentiles, not dollar amounts.
export const INCOME_RANGE_OPTIONS: MenuItem<IncomeRangeTypeValue>[] = [
  { value: IncomeRangeType.INCOME_RANGE_90_UP, label: 'Top 10%' },
  { value: IncomeRangeType.INCOME_RANGE_80_90, label: '11–20%' },
  { value: IncomeRangeType.INCOME_RANGE_70_80, label: '21–30%' },
  { value: IncomeRangeType.INCOME_RANGE_60_70, label: '31–40%' },
  { value: IncomeRangeType.INCOME_RANGE_50_60, label: '41–50%' },
  { value: IncomeRangeType.INCOME_RANGE_0_50, label: 'Lower 50%' }
];

export const DEVICE_OPTIONS: MenuItem<DeviceCriterionValue>[] = [
  { value: DeviceCriterion.MOBILE, label: 'Mobile' },
  { value: DeviceCriterion.TABLET, label: 'Tablet' },
  { value: DeviceCriterion.DESKTOP, label: 'Desktop' },
  { value: DeviceCriterion.CONNECTED_TV, label: 'Connected TV' }
];

// Google returns at most this many creators per search, and broad searches always hit it.
export const TOP_CREATORS_LIMIT = 100;

// The partnership programs shown as badges. PARTNERSHIP_ADS is left out because nearly every
// creator has it. CREATOR_PARTNERSHIPS is YouTube BrandConnect. The partnership type is
// open-ended, so anything not listed here is ignored.
export const PARTNERSHIP_BADGE_LABELS: Partial<Record<string, string>> = {
  [PartnershipOpportunity.CREATOR_PARTNERSHIPS]: 'BrandConnect',
  [PartnershipOpportunity.YOUTUBE_SELECT_LINEUPS]: 'YouTube Select',
  [PartnershipOpportunity.CREATOR_TAKEOVER]: 'Creator Takeover'
};
export const ACTIVE_SHORTS_BADGE_LABEL = 'Active Shorts';

export const ViewerDetails = {
  SHARED: 'Shared',
  NOT_SHARED: 'No viewer details'
} as const;
export type ViewerDetailsValue = (typeof ViewerDetails)[keyof typeof ViewerDetails];

export const VIEWER_DETAILS_TOOLTIP =
  'Shows if the creator shares viewer data with Google. Avg. views and engagement are only ' +
  'available for creators that share it. The other creators show "—" in these columns.';

// One search result row: a creator channel, with its counts parsed so they sort numerically.
// Metrics Google only returns for channels that share viewer data are undefined otherwise, so
// they render "—" rather than 0.
export interface CreatorRow {
  channelId: string;
  displayName: string;
  handle: string;
  channelUrl: string;
  thumbnailUrl: string;
  subscribers: number;
  views: number;
  videos: number;
  averageViews?: number;
  engagementRate?: number; // 0–1
  relevance?: number; // 0–1, topic searches only
  viewerDetails: ViewerDetailsValue;
  badges: string[];
  // Returned in Google's local-creator list (LOCAL_CREATOR_DATA).
  isLocal: boolean;
  // The channel as Google returned it, for saving it to a set.
  channel: YouTubeChannelInsights;
}

// Where "Save to set" puts the selected creators.
export const SaveTarget = {
  NEW: 'new',
  EXISTING: 'existing'
} as const;
export type SaveTargetValue = (typeof SaveTarget)[keyof typeof SaveTarget];

export const SAVE_TARGET_OPTIONS: { key: SaveTargetValue; label: string }[] = [
  { key: SaveTarget.NEW, label: 'New set' },
  { key: SaveTarget.EXISTING, label: 'Existing set' }
];
